import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Auth } from "aws-amplify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Page,
  Container,
  Stack,
  Typography,
  Button,
  Divider,
  Grid,
  TextField,
  InputAdornment,
  Switch,
  Autocomplete,
  Box,
  Checkbox,
  Tooltip,
} from "@common-ui/ui";
import { Tv, Info } from "@mui/icons-material";
import { FormControlLabel } from "@mui/material";

import { Header } from "../../components/Header";

import Address from "../org/Address";

import { countries, createUserProfile } from "../../services";

export const CreateUserProfile = () => {
  const history = useHistory();
  const [user, setUser] = useState(null);
  const salutationsOptions = [
    {
      label: "Mr.",
      value: "Mr.",
    },
    {
      label: "Mrs.",
      value: "Mrs.",
    },
    {
      label: "Ms.",
      value: "Ms.",
    },
    {
      label: "Dr.",
      value: "Dr.",
    },
  ];

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      middle_initial: "",
      salutation: null,
      name_private: false,
      display_name: user,
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: null,
      postal_code: "",
      mailing_address_private: false,
      shipping_address_line_1: "",
      shipping_address_line_2: "",
      shipping_city: "",
      shipping_state: "",
      shipping_country: null,
      shipping_postal_code: "",
      shipping_address_private: false,
      shipping_same_as_mailing: false,
    },
    validationSchema: Yup.object({
      first_name: Yup.string().trim().max(100).required(),
      last_name: Yup.string().trim().max(100).required(),
      middle_initial: Yup.string().nullable().trim().length(1),
      salutation: Yup.object().nullable(),
      name_private: Yup.boolean(),
      display_name: Yup.string()
        .trim()
        .min(3, "Display name cannot be less than 3 characters long")
        .max(20, "Display name cannot be more than 20 characters long")
        .required("Display name is required"),
      address_line_1: Yup.string().trim(),
      address_line_2: Yup.string().trim(),
      city: Yup.string().trim(),
      state: Yup.string().trim(),
      country: Yup.object().nullable(),
      postal_code: Yup.string().trim(),
      shipping_address_line_1: Yup.string().trim(),
      shipping_address_line_2: Yup.string().trim(),
      shipping_city: Yup.string().trim(),
      shipping_state: Yup.string().trim(),
      shipping_country: Yup.object().nullable(),
      shipping_postal_code: Yup.string().trim(),
      shipping_same_as_mailing: Yup.boolean(),
    }),
    onSubmit: () => {
      let {
        first_name,
        last_name,
        middle_initial,
        salutation,
        name_private,
        display_name,

        mailing_address_private,
        address_line_1,
        address_line_2,
        city,
        state,
        country,
        postal_code,
        shipping_address_private,
        shipping_address_line_1,
        shipping_address_line_2,
        shipping_city,
        shipping_state,
        shipping_country,
        shipping_postal_code,
        shipping_same_as_mailing,
      } = formik.values;

      let attributes = {
        first_name,
        last_name,
        middle_initial: middle_initial ?? " ",
        salutation: salutation?.label || " ",
        name_private: name_private,
        display_name,

        mailing_address_private: mailing_address_private,
        shipping_address_private: shipping_address_private,
        shipping_same_as_mailing: shipping_same_as_mailing,
        mailing_address: {
          address_line_1: address_line_1 ? address_line_1 : " ",
          address_line_2: address_line_2 ? address_line_2 : " ",
          city: city ? city : " ",
          state_or_province: state ? state : " ",
          country_or_region: country?.label || " ",
          postal_code: postal_code ? postal_code : " ",
        },
        shipping_address: {
          address_line_1: shipping_address_line_1
            ? shipping_address_line_1
            : " ",
          address_line_2: shipping_address_line_2
            ? shipping_address_line_2
            : " ",
          city: shipping_city ? shipping_city : " ",
          state_or_province: shipping_state ? shipping_state : " ",
          country_or_region: shipping_country?.label || " ",
          postal_code: shipping_postal_code ? shipping_postal_code : " ",
        },
      };

      submit(attributes);
    },
  });

  const submit = async (attributes) => {
    try {
      const status = await createUserProfile(attributes);
      if (status === 200 || status === 204) {
        history.push({
          pathname: "/user-profile",
          state: {
            from: "/create-user-profile",
          },
        });
      }
    } catch (error) {
      alert(error);
    }
  };

  useEffect(() => {
    async function getUser() {
      try {
        const user = await Auth.currentAuthenticatedUser();
        formik.setFieldValue("display_name", user?.username);
        setUser(user);
      } catch (error) {
        console.log(error);
      }
    }

    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formik.values?.shipping_same_as_mailing) {
      formik.setFieldValue(
        "shipping_address_line_1",
        formik.values.address_line_1
      );
      formik.setFieldValue(
        "shipping_address_line_2",
        formik.values.address_line_2
      );
      formik.setFieldValue("shipping_city", formik.values.city);
      formik.setFieldValue("shipping_state", formik.values.state);
      formik.setFieldValue("shipping_postal_code", formik.values.postal_code);
      formik.setFieldValue("shipping_country", formik.values.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.shipping_same_as_mailing,
    formik.values.address_line_1,
    formik.values.address_line_2,
    formik.values.city,
    formik.values.state,
    formik.values.country,
    formik.values.postal_code,
  ]);

  return (
    <Page title="FlukeWorks">
      <Header xl name="Create My Profile" history={history} />
      <form onSubmit={formik.handleSubmit}>
        <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
          <Typography variant="h4">Personal Information</Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 3 }}
          >
            <Grid item md={6} xs={12}>
              {" "}
              <div>
                <Typography>
                  <svg
                    width="16px"
                    height="16px"
                    viewBox="0 0 16 16"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g fillRule="evenodd">
                      <polygon
                        id="Shape"
                        fill="#EE2E24"
                        fillRule="nonzero"
                        points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                      ></polygon>
                    </g>
                  </svg>
                  Display Name{" "}
                  <span>
                    <Tooltip
                      placement="right"
                      arrow
                      title="You can choose any display name you want, 
                        using a combination of letters, underscores “_” or dashes “-”. 
                        The display name is how you will appear to other users, 
                        so keep it business and family friendly. 
                        Your username is the default display name"
                    >
                      <Info sx={{ fontSize: 14, color: "#777777" }} />
                    </Tooltip>
                  </span>
                </Typography>
                <Tooltip
                  title="Your display name is how you will appear to other users in comments, posts, and events."
                  placement="bottom"
                  arrow
                >
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tv />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.display_name && formik.errors.display_name
                    )}
                    fullWidth
                    helperText={
                      formik.touched.display_name && formik.errors.display_name
                    }
                    placeholder="Type"
                    margin="normal"
                    name="display_name"
                    onBlur={formik.handleBlur}
                    onChange={(event) => {
                      if (
                        // eslint-disable-next-line no-useless-escape
                        /\d|[`!@#$%^&*()+\=\[\]{};':"\\|,.<>\/?~]/.test(
                          event.target?.value
                        )
                      ) {
                        formik.setFieldError(
                          "display_name",
                          "Display Name cannot have numbers or special characters other than '-' or '_'"
                        );
                      } else {
                        formik.setFieldValue(
                          "display_name",
                          event.target.value
                        );
                      }
                    }}
                    value={formik.values.display_name}
                    variant="outlined"
                  />
                </Tooltip>
              </div>
            </Grid>
          </Stack>
        </Container>

        <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 3 }}
          >
            <Typography variant="h4">Full Name</Typography>
            <FormControlLabel
              control={<Switch />}
              label="Private"
              name="name_private"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name_private}
              checked={formik.values.name_private}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            //   alignItems="center"
            justifyContent="space-between"
          >
            <div style={{ minWidth: "10%", maxWidth: "15%" }}>
              <Typography>Salutation</Typography>
              <Autocomplete
                id="salutations-select"
                sx={{ mt: 1 }}
                disablePortal
                ListboxProps={{ style: { maxHeight: "200px" } }}
                options={salutationsOptions}
                autoHighlight
                size="small"
                getOptionLabel={(option) => option.label}
                margin="normal"
                name="salutation"
                limitTags={4}
                onBlur={formik.handleBlur}
                onChange={(value, newValue) => {
                  formik.setFieldValue("salutation", newValue);
                }}
                fullWidth
                value={formik.values.salutation}
                //   defaultValue={formik.values.salutation}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                    {...props}
                  >
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select preferred salutation"
                    helperText={
                      formik.touched.salutation && formik.errors.salutation
                    }
                    error={Boolean(
                      formik.touched.salutation && formik.errors.salutation
                    )}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off", // disable autocomplete and autofill
                    }}
                  />
                )}
              />
            </div>
            <div style={{ minWidth: "30%" }}>
              <Typography>
                <svg
                  width="16px"
                  height="16px"
                  viewBox="0 0 16 16"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g fillRule="evenodd">
                    <polygon
                      id="Shape"
                      fill="#EE2E24"
                      fillRule="nonzero"
                      points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                    ></polygon>
                  </g>
                </svg>
                First Name
              </Typography>
              <TextField
                sx={{ mt: 1 }}
                size="small"
                autoComplete="off"
                error={Boolean(
                  formik.touched.first_name && formik.errors.first_name
                )}
                fullWidth
                helperText={
                  formik.touched.first_name && formik.errors.first_name
                }
                placeholder="Type"
                margin="normal"
                name="first_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.first_name}
                variant="outlined"
              />
            </div>
            <div style={{ maxWidth: "10%" }}>
              <Typography>Middle Initial</Typography>
              <TextField
                sx={{ mt: 1 }}
                size="small"
                autoComplete="off"
                error={Boolean(
                  formik.touched.middle_initial && formik.errors.middle_initial
                )}
                fullWidth
                helperText={
                  formik.touched.middle_initial && formik.errors.middle_initial
                }
                placeholder="Type"
                margin="normal"
                name="middle_initial"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.middle_initial}
                variant="outlined"
              />
            </div>
            <div style={{ minWidth: "30%" }}>
              <Typography>
                <svg
                  width="16px"
                  height="16px"
                  viewBox="0 0 16 16"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g fillRule="evenodd">
                    <polygon
                      id="Shape"
                      fill="#EE2E24"
                      fillRule="nonzero"
                      points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                    ></polygon>
                  </g>
                </svg>
                Last Name
              </Typography>
              <TextField
                sx={{ mt: 1 }}
                size="small"
                autoComplete="off"
                error={Boolean(
                  formik.touched.last_name && formik.errors.last_name
                )}
                fullWidth
                helperText={formik.touched.last_name && formik.errors.last_name}
                placeholder="Type"
                margin="normal"
                name="last_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.last_name}
                variant="outlined"
              />
            </div>
          </Stack>
          <Divider sx={{ py: 1 }} />
        </Container>

        <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
          <Typography variant="h4" sx={{ my: 3 }}>
            Addresses
          </Typography>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h5">Mailing Address</Typography>
                <FormControlLabel
                  control={<Switch />}
                  label="Private"
                  name="mailing_address_private"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.mailing_address_private}
                  checked={formik.values.mailing_address_private}
                />
              </Stack>
              <Stack direction="column">
                <div>
                  <Typography sx={{ mt: 3 }}>Address 1</Typography>
                  <Address formik={formik} field="address_line_1" />
                </div>
                {/* </Container> */}
                {/* <Container> */}
                <Typography sx={{ mt: 1 }}>Address 2</Typography>
                <TextField
                  autoComplete="off"
                  size="small"
                  error={Boolean(
                    formik.touched.address_line_2 &&
                      formik.errors.address_line_2
                  )}
                  fullWidth
                  helperText={
                    formik.touched.address_line_2 &&
                    formik.errors.address_line_2
                  }
                  placeholder="Type"
                  // label="Company or Organization Name"
                  margin="normal"
                  sx={{ mt: 1 }}
                  name="address_line_2"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.address_line_2}
                />
                <div>
                  <Typography>City</Typography>
                  <Address formik={formik} field="city" />
                </div>
                <div>
                  <Typography sx={{ mt: 1 }}>State/Province</Typography>
                  <Address formik={formik} field="state" />
                </div>
                <div>
                  <Typography sx={{ mt: 1 }}>Country / Region</Typography>
                  <Autocomplete
                    id="country"
                    sx={{ mt: 1 }}
                    disablePortal
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={countries}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option.label}
                    margin="normal"
                    name="country"
                    limitTags={4}
                    onBlur={formik.handleBlur}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("country", newValue);
                    }}
                    fullWidth
                    value={formik.values.country}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                        {option.label} ({option.code}) +{option.phone}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select"
                        helperText={
                          formik.touched.country && formik.errors.country
                        }
                        error={Boolean(
                          formik.touched.country && formik.errors.country
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
                <div>
                  <Typography sx={{ mt: 1 }}>Zip/Postal Code</Typography>
                  <Address formik={formik} field="postal_code" />
                </div>
              </Stack>
              {/* </Container> */}
              {/* </Grid> */}
            </Grid>
            <Grid item md={6} xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h5">Shipping Address</Typography>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Same as Mailing address"
                    name="shipping_same_as_mailing"
                    onBlur={formik.handleBlur}
                    onChange={(event) => {
                      formik.setFieldValue(
                        "shipping_same_as_mailing",
                        event.target.checked
                      );
                    }}
                    value={formik.values.shipping_same_as_mailing}
                    checked={formik.values.shipping_same_as_mailing}
                    sx={{ mr: 0.5 }}
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="Private"
                    name="shipping_address_private"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.shipping_address_private}
                    checked={formik.values.shipping_address_private}
                  />
                </Stack>
              </Stack>
              <Stack direction="column">
                <div>
                  <Typography sx={{ mt: 3 }}>Address 1</Typography>
                  <Address formik={formik} field="shipping_address_line_1" />
                </div>
                {/* </Container> */}
                {/* <Container> */}
                <Typography sx={{ mt: 1 }}>Address 2</Typography>
                <TextField
                  autoComplete="off"
                  size="small"
                  error={Boolean(
                    formik.touched.shipping_address_line_2 &&
                      formik.errors.shipping_address_line_2
                  )}
                  fullWidth
                  helperText={
                    formik.touched.shipping_address_line_2 &&
                    formik.errors.shipping_address_line_2
                  }
                  placeholder="Type"
                  // label="Company or Organization Name"
                  margin="normal"
                  sx={{ mt: 1 }}
                  name="shipping_address_line_2"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.shipping_address_line_2}
                />
                <div>
                  <Typography>City</Typography>
                  <Address formik={formik} field="shipping_city" />
                </div>
                <div>
                  <Typography sx={{ mt: 1 }}>State/Province</Typography>
                  <Address formik={formik} field="shipping_state" />
                </div>
                <div>
                  <Typography sx={{ mt: 1 }}>Country / Region</Typography>
                  <Autocomplete
                    id="country"
                    sx={{ mt: 1 }}
                    disablePortal
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={countries}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option.label}
                    margin="normal"
                    name="shipping_country"
                    limitTags={4}
                    onBlur={formik.handleBlur}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("shipping_country", newValue);
                    }}
                    fullWidth
                    value={formik.values.shipping_country}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                        {option.label} ({option.code}) +{option.phone}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select"
                        helperText={
                          formik.touched.shipping_country &&
                          formik.errors.shipping_country
                        }
                        error={Boolean(
                          formik.touched.shipping_country &&
                            formik.errors.shipping_country
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
                <div>
                  <Typography sx={{ mt: 1 }}>Zip/Postal Code</Typography>
                  <Address formik={formik} field="shipping_postal_code" />
                </div>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ py: 1 }} />
        </Container>
        <Container maxWidth="md" component="main">
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="flex-end"
            spacing={{ xs: 1, md: 2 }}
            sx={{ py: 2 }}
          >
            <div>
              <Button
                color="primary"
                fullWidth
                size="large"
                type="reset"
                onClick={() => {
                  formik.resetForm();
                }}
                variant="text"
              >
                Clear
              </Button>
            </div>
            <div>
              <Button
                color="primary"
                disabled={
                  formik.isSubmitting || !(formik.isValid && formik.dirty)
                }
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Save
              </Button>
            </div>
          </Stack>
        </Container>
      </form>
    </Page>
  );
};
